/**
 * The one Speed control for every <audio> and <video> the Hub plays itself.
 *
 * Bobbie, 2026-10-14: a staff member asked for "a .75 and a .85 speed", and
 * Bobbie asked "Is that doable to speed up videos?". Built ONCE, here, and
 * used by every place the Hub renders its own media element:
 * MediaEmbedPlayer (which already serves the class page Music & Video tab,
 * Solo Music and All Music, the family Videos page, class posts, tasks and
 * both dancer views), message attachments (AudioWithSpeed below) and the
 * public class page (AudioWithSpeed again). A YouTube, Vimeo or Drive link
 * plays in that service's own player inside an iframe, which the Hub cannot
 * reach into, so it never gets this control.
 *
 * Three buttons from lib/playbackSpeed.ts's one list (0.75x, 0.85x, Normal),
 * aria-pressed on the chosen one, each at least 44px, in a group labelled
 * Speed. The label turns into "Speed: 0.75x" on a lime pill whenever the
 * speed is not Normal, so a slowed song is never mistaken for the real tempo.
 *
 * No reads, no writes, nothing stored: the speed lives in this component's
 * state only, so every player starts at Normal each time a page opens, and
 * one player's speed never touches another's. A player handed a DIFFERENT
 * file (a new item in the same spot on the page, keyed or not) starts again
 * at Normal too; the same file on a re-signed link keeps its speed.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  NORMAL_PLAYBACK_SPEED,
  PLAYBACK_SPEEDS,
  applyPlaybackSpeed,
  isNormalSpeed,
  playbackItemKey,
  playbackSpeedLabel,
  rateChangeAction,
  sameSpeed,
} from '../lib/playbackSpeed';

// Browsers set the rate back to defaultPlaybackRate whenever a source loads,
// and some older engines have reset it to 1 at load or at play. Putting the
// chosen speed back on at each of these is a no-op when nothing moved
// (applyPlaybackSpeed only assigns a value that differs).
const REAPPLY_EVENTS = ['loadstart', 'emptied', 'loadedmetadata', 'canplay', 'play'] as const;

// The window rateChangeAction's RESETS_UNDONE_PER_SECOND counts over.
const RESET_WINDOW_MS = 1000;

export interface PlaybackSpeedState {
  /** The speed this player is at now. Starts at Normal on every mount. */
  speed: number;
  /** What the Speed buttons call. Works with or without an element yet. */
  choose: (speed: number) => void;
  /**
   * Hand this to the <audio> or <video> as its `ref`. It applies the chosen
   * speed the moment the element exists, so a speed picked BEFORE a video's
   * Play button mounts the element is in force from the first frame, and it
   * keeps that speed through a new source, a reload and a Try again remount.
   */
  mediaRef: (el: HTMLMediaElement | null) => void;
}

/**
 * `itemKey` names the file this player is playing (playbackItemKey of its
 * url). When it changes, the player has been handed a DIFFERENT item, and the
 * speed goes back to Normal: a slowed practice speed picked for one track
 * never carries onto another, whether or not the caller keyed the player.
 * The same file on a re-signed link has the same key and keeps its speed.
 */
export function usePlaybackSpeed(itemKey?: string): PlaybackSpeedState {
  const [speed, setSpeed] = useState<number>(NORMAL_PLAYBACK_SPEED);
  // The chosen speed, readable synchronously from the element's own event
  // listeners (state would be one render behind).
  const speedRef = useRef<number>(NORMAL_PLAYBACK_SPEED);
  const elementRef = useRef<HTMLMediaElement | null>(null);
  const detachRef = useRef<(() => void) | null>(null);
  // When each recent reset was undone, for rateChangeAction's per-second cap.
  const resetTimesRef = useRef<number[]>([]);
  const itemKeyRef = useRef<string | undefined>(itemKey);

  // A different item: back to Normal. A layout effect, so it lands after the
  // element has its new src but before the browser paints the old speed
  // beside the new item or runs the new load's events (which reapply
  // speedRef, so it has to say Normal by then).
  useLayoutEffect(() => {
    if (itemKeyRef.current === itemKey) return;
    itemKeyRef.current = itemKey;
    resetTimesRef.current = [];
    if (isNormalSpeed(speedRef.current)) return;
    speedRef.current = NORMAL_PLAYBACK_SPEED;
    setSpeed(NORMAL_PLAYBACK_SPEED);
    const el = elementRef.current;
    if (el) applyPlaybackSpeed(el, NORMAL_PLAYBACK_SPEED);
  }, [itemKey]);

  const mediaRef = useCallback((el: HTMLMediaElement | null) => {
    detachRef.current?.();
    detachRef.current = null;
    elementRef.current = el;
    if (!el) return;

    const reapply = () => applyPlaybackSpeed(el, speedRef.current);
    const onRateChange = () => {
      const now = Date.now();
      const recent = resetTimesRef.current.filter((at) => now - at < RESET_WINDOW_MS);
      resetTimesRef.current = recent;
      const action = rateChangeAction(el.playbackRate, speedRef.current, el.readyState, el.defaultPlaybackRate, recent.length);
      if (action === 'reapply') {
        // A reset (mid-load, or an engine dropping the rate to a literal 1).
        recent.push(now);
        reapply();
      } else if (action === 'follow') {
        // The browser's own speed menu moved it (or an engine kept snapping
        // it back faster than it could be undone). Follow, so the buttons
        // never claim a speed the track is not really playing at.
        const followed = el.playbackRate;
        speedRef.current = followed;
        setSpeed(followed);
        applyPlaybackSpeed(el, followed);
      }
    };

    for (const type of REAPPLY_EVENTS) el.addEventListener(type, reapply);
    el.addEventListener('ratechange', onRateChange);
    reapply();

    detachRef.current = () => {
      for (const type of REAPPLY_EVENTS) el.removeEventListener(type, reapply);
      el.removeEventListener('ratechange', onRateChange);
    };
  }, []);

  const choose = useCallback((next: number) => {
    if (!Number.isFinite(next) || next <= 0) return;
    resetTimesRef.current = [];
    speedRef.current = next;
    setSpeed(next);
    const el = elementRef.current;
    if (el) applyPlaybackSpeed(el, next);
  }, []);

  return { speed, choose, mediaRef };
}

// The label sits on its own line above the buttons, at one fixed height in
// both of its looks, so tapping a speed never moves a button out from under
// the thumb. (Side by side, "Speed: 0.75x" is wider than "Speed" and pushed
// the buttons onto a new line on a narrow phone the moment one was tapped.)
const groupStyle: CSSProperties = {
  display: 'grid',
  justifyItems: 'start',
  gap: 4,
  minWidth: 0,
};

// The label carries its own card surface (var(--paper)), so it reads on
// whatever is behind the player. On a card it is invisible as a shape and
// reads as plain text; on a page wash that does not flip with the theme (the
// younger dancer's class page keeps its light colour in dark mode) it is a
// small dark pill instead of light text on a light wash.
const labelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  height: 24,
  padding: '0 8px',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--paper)',
  fontSize: 12,
  fontWeight: 800,
  color: 'var(--ink-soft)',
  whiteSpace: 'nowrap',
};

// Not Normal: the label becomes a pill naming the speed. lime-deep on a
// lime-wash tint over the card surface is the studio's "write with green"
// pair and clears 4.5:1 on both themes (lime-deep flips to the bright green
// in dark mode); the paper underneath keeps the tint opaque on any page.
const slowedLabelStyle: CSSProperties = {
  ...labelStyle,
  padding: '0 10px',
  background: 'linear-gradient(var(--lime-wash), var(--lime-wash)), var(--paper)',
  border: '1px solid var(--lime-deep)',
  color: 'var(--lime-deep)',
};

// The row WRAPS. Side by side the three buttons are about 217px, and a
// player does not always get that much: an older cut on a class page's
// Music & Video tab is a half-width tile (about 150px inside at 390, less at
// 320), and a task's video attachment tile is narrower still. Kept on one
// line there, Normal ran out of its tile, under the next tile, and off the
// side of the page. Wrapping keeps every button inside its own player's
// card. It never moves a button when a speed is tapped: every button keeps
// the same 2px edge and the same text whether chosen or not, so where the
// row breaks depends only on how wide the card is.
const buttonRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  minWidth: 0,
  maxWidth: '100%',
};

// The app's chip convention (DancerPracticeCard's chipStyle): the chosen one
// wears a 2px ink edge. A chosen SLOWER speed is also filled studio green, so
// it is the loud one; a chosen Normal stays on the card surface, so a page of
// players all at Normal is calm and the one that is slowed stands out.
// studio-green is a FILL and always carries the fixed dark primary-on ink, on
// both themes.
function speedButtonStyle(pressed: boolean, slowed: boolean): CSSProperties {
  const filled = pressed && slowed;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    minHeight: 'var(--tap-target)',
    minWidth: 'var(--tap-target)',
    padding: '0 12px',
    borderRadius: 'var(--radius-pill)',
    border: pressed ? '2px solid var(--ink)' : '2px solid var(--line)',
    background: filled ? 'var(--studio-green)' : 'var(--paper)',
    color: filled ? 'var(--primary-on)' : 'var(--ink)',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    cursor: 'pointer',
  };
}

export function PlaybackSpeedControl({
  speed,
  onChoose,
  title,
}: {
  speed: number;
  onChoose: (speed: number) => void;
  /** The track or video's own name, for a screen reader landing here directly. */
  title?: string;
}) {
  const slowed = !isNormalSpeed(speed);
  const current = playbackSpeedLabel(speed);
  return (
    <div
      role="group"
      aria-label={title ? `Speed for ${title}` : 'Speed'}
      data-playback-speed={current}
      style={groupStyle}
    >
      <span style={slowed ? slowedLabelStyle : labelStyle}>
        {slowed ? `Speed: ${current}` : 'Speed'}
      </span>
      <div style={buttonRowStyle}>
        {PLAYBACK_SPEEDS.map((option) => {
          const pressed = sameSpeed(option, speed);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={pressed}
              onClick={() => onChoose(option)}
              style={speedButtonStyle(pressed, !isNormalSpeed(option))}
            >
              {playbackSpeedLabel(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const audioWithSpeedWrapStyle: CSSProperties = {
  display: 'grid',
  gap: 6,
  minWidth: 0,
};

/**
 * A plain <audio controls> with the Speed control under it, for the two
 * places that draw their own audio element rather than going through
 * MediaEmbedPlayer: a message's audio attachment and the public class page's
 * music. Everything about the element itself (preload, style, fallback
 * content) stays exactly what the caller passes.
 */
export function AudioWithSpeed({
  src,
  title,
  preload = 'none',
  style,
  children,
}: {
  src: string;
  /** The track's name: the element's aria-label and the Speed group's. */
  title?: string;
  preload?: 'none' | 'metadata' | 'auto';
  style?: CSSProperties;
  /** Fallback content for a browser with no audio element at all. */
  children?: ReactNode;
}) {
  const { speed, choose, mediaRef } = usePlaybackSpeed(playbackItemKey(src));
  return (
    <div style={audioWithSpeedWrapStyle}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- studio audio, no captions to carry */}
      <audio
        ref={mediaRef}
        controls
        preload={preload}
        src={src}
        style={style}
        aria-label={title || undefined}
      >
        {children}
      </audio>
      <PlaybackSpeedControl speed={speed} onChoose={choose} title={title} />
    </div>
  );
}
